import api, {CSV_IMPORT_ACTIVITY_TYPE} from '../Api';

// Contract for the "From CSV" company/contact/location import — mirrors the API's V_7.0 CsvImport models.

export type CsvImportType = 'company' | 'contact' | 'location';

export interface ICsvImportUploadRequest {
    fileName: string;
    /** Base64-encoded CSV content; the first row must be a header row. */
    file: string;
}

export interface ICsvImportUploadResponse {
    success: boolean;
    error?: string;
    /** Temporary id of the parked file; pass it to runCsvImport. */
    fileId?: string;
    fileName?: string;
    headers?: string[];
    rowCount: number;
    /** The first few data rows (values in header order), for the mapping preview. */
    sampleRows?: string[][];
}

export interface ICsvImportRunRequest {
    fileId: string;
    fileName?: string;
    importType: CsvImportType;
    /** User-chosen source name; row ids are recorded as external mappings under it (case-insensitive). */
    referenceName: string;
    /** Record field key → CSV column header. */
    fieldMappings: {[field: string]: string};
}

export interface ICsvImportResult {
    success: boolean;
    importId?: string;
    rows: number;
    created: number;
    updated: number;
    skipped: number;
    errored: number;
    errors?: string[];
}

/** One past CSV import for the Activity view. */
export interface ICsvImportActivity {
    id: string;
    importType?: CsvImportType;
    fileName?: string;
    referenceName?: string;
    username?: string;
    started?: string;
    completed?: string;
    success: boolean;
    rows: number;
    created: number;
    updated: number;
    skipped: number;
    errored: number;
    errors?: string[];
}

const csvImportEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        uploadCsvImportFile: build.mutation<ICsvImportUploadResponse, ICsvImportUploadRequest>({
            query: (body) => ({
                url: 'contacts/import/csv',
                method: 'POST',
                body
            })
        }),
        runCsvImport: build.mutation<ICsvImportResult, ICsvImportRunRequest>({
            query: (body) => ({
                url: 'contacts/import/csv/run',
                method: 'POST',
                body
            }),
            invalidatesTags: [CSV_IMPORT_ACTIVITY_TYPE]
        }),
        getCsvImportActivity: build.query<{items: ICsvImportActivity[]; total: number}, void>({
            query: () => 'contacts/import/csv/activity',
            providesTags: [CSV_IMPORT_ACTIVITY_TYPE]
        })
    })
});

export const {
    useUploadCsvImportFileMutation,
    useRunCsvImportMutation,
    useGetCsvImportActivityQuery
} = csvImportEndpoints;
